// src/pages/trading-portal/indicators/IndicatorsPage.js
// Technical indicators management page

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    IconButton,
    Chip,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Tooltip,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Autocomplete,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Calculate as CalculateIcon,
    ShowChart as ShowChartIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    IndicatorType,
    IndicatorTypeLabels,
    TimeFrame,
    TimeFrameLabels,
    SignalType,
    SignalTypeLabels,
    SignalTypeColors,
} from '../stockTradingEnums';

const IndicatorsPage = () => {
    // Permission checks
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    // State
    const [indicators, setIndicators] = useState([]);
    const [symbols, setSymbols] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [symbolFilter, setSymbolFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [signalFilter, setSignalFilter] = useState('');
    const [timeframeFilter, setTimeframeFilter] = useState('');

    // Dialog states
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedIndicator, setSelectedIndicator] = useState(null);
    const [calculating, setCalculating] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        symbol_id: '',
        indicator_type: '',
        parameters: '{}',
        timeframe: 'DAILY',
    });
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const checkPermissions = async () => {
            const view = await hasPermission('tr-indicators', 'view');
            const add = await hasPermission('tr-indicators', 'add');
            const del = await hasPermission('tr-indicators', 'delete');
            setCanView(view);
            setCanAdd(add);
            setCanDelete(del);
        };
        checkPermissions();
    }, []);

    // Fetch symbols for dropdown
    const fetchSymbols = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/stock-trading/symbols?per_page=200');
            if (response.data.success) {
                setSymbols(response.data.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch symbols:', err);
        }
    }, []);

    // Fetch indicators
    const fetchIndicators = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        setError(null);

        try {
            // Every filter is server-side. The table is server-paginated, so
            // filtering the visible page would report a wrong total and hide
            // matches on every other page.
            const params = new URLSearchParams({ page: page + 1, per_page: rowsPerPage });
            if (symbolFilter) params.append('symbol_id', symbolFilter);
            if (typeFilter) params.append('indicator_type', typeFilter);
            if (signalFilter) params.append('signal', signalFilter);
            if (timeframeFilter) params.append('timeframe', timeframeFilter);

            const response = await axiosInstance.get(`/api/stock-trading/indicators?${params.toString()}`);

            if (response.data.success) {
                setIndicators(response.data.data || []);
                setTotalCount(response.data.pagination?.total || 0);
            } else {
                setError(response.data.message || 'Failed to fetch indicators');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch indicators');
        } finally {
            setLoading(false);
        }
    }, [canView, page, rowsPerPage, symbolFilter, typeFilter, signalFilter, timeframeFilter]);

    useEffect(() => {
        if (canView) {
            fetchSymbols();
            fetchIndicators();
        }
    }, [fetchIndicators, fetchSymbols, canView]);

    // Handlers
    const hasActiveFilters = Boolean(symbolFilter || typeFilter || signalFilter || timeframeFilter);

    const handleClearFilters = () => {
        setSymbolFilter('');
        setTypeFilter('');
        setSignalFilter('');
        setTimeframeFilter('');
        setPage(0);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };

    const resetForm = () => {
        setFormData({ symbol_id: '', indicator_type: '', parameters: '{}', timeframe: 'DAILY' });
        setFormErrors({});
    };

    const handleCreateOpen = () => { resetForm(); setCreateDialogOpen(true); };
    const handleCreateClose = () => { setCreateDialogOpen(false); resetForm(); };

    const handleFormChange = (field) => (event) => {
        setFormData(prev => ({ ...prev, [field]: event.target.value }));
        if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: null }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.symbol_id) errors.symbol_id = 'Symbol is required';
        if (!formData.indicator_type) errors.indicator_type = 'Indicator type is required';
        try {
            JSON.parse(formData.parameters);
        } catch {
            errors.parameters = 'Invalid JSON format';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateSubmit = async () => {
        if (!validateForm()) return;
        setSubmitting(true);

        try {
            const payload = {
                ...formData,
                parameters: JSON.parse(formData.parameters),
            };
            const response = await axiosInstance.post('/api/stock-trading/indicators', payload);

            if (response.data.success) {
                const newIndicatorId = response.data.data?.id;
                handleCreateClose();

                // Auto-calculate the newly created indicator
                if (newIndicatorId) {
                    try {
                        await axiosInstance.post(`/api/stock-trading/indicators/${newIndicatorId}/calculate`);
                    } catch (calcErr) {
                        console.error('Auto-calculate failed:', calcErr);
                    }
                }

                fetchIndicators();
            } else {
                setFormErrors({ submit: response.data.message || 'Failed to create indicator' });
            }
        } catch (err) {
            setFormErrors({ submit: err.response?.data?.message || 'Failed to create indicator' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (indicator) => { setSelectedIndicator(indicator); setDeleteDialogOpen(true); };

    const handleDeleteConfirm = async () => {
        if (!selectedIndicator) return;
        try {
            const response = await axiosInstance.delete(`/api/stock-trading/indicators/${selectedIndicator.id}`);
            if (response.data.success) {
                fetchIndicators();
                setDeleteDialogOpen(false);
                setSelectedIndicator(null);
            } else {
                setError(response.data.message || 'Failed to delete indicator');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete indicator');
        }
    };

    const handleCalculate = async (indicator) => {
        setCalculating(indicator.id);
        try {
            await axiosInstance.post(`/api/stock-trading/indicators/${indicator.id}/calculate`);
            fetchIndicators();
        } catch (err) {
            setError(err.response?.data?.message || 'Calculation failed');
        } finally {
            setCalculating(null);
        }
    };

    const getSignalChip = (signal) => {
        if (!signal) return null;
        const upperSignal = signal.toUpperCase();
        return (
            <Chip
                label={SignalTypeLabels[upperSignal] || signal}
                size="small"
                color={SignalTypeColors[upperSignal] || 'default'}
                sx={{ fontWeight: 600 }}
            />
        );
    };

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view indicators.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h4" component="h1">Technical Indicators</Typography>
                    <Chip label={totalCount} color="primary" size="small" />
                </Box>
                {canAdd && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateOpen}
                        sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
                        Add Indicator
                    </Button>
                )}
            </Box>

            {/* Filters — all server-side; each one resets to page 0 so the pager
                never lands on a page that no longer exists under the new filter. */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Symbol</InputLabel>
                            <Select
                                value={symbolFilter}
                                label="Symbol"
                                onChange={(e) => { setSymbolFilter(e.target.value); setPage(0); }}
                            >
                                <MenuItem value="">All Symbols</MenuItem>
                                {symbols.map(sym => (
                                    <MenuItem key={sym.id} value={sym.id}>{sym.symbol}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={typeFilter}
                                label="Type"
                                onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
                            >
                                <MenuItem value="">All Types</MenuItem>
                                {/* Driven off the shared enum so a new indicator type
                                    appears here without a second list to maintain. */}
                                {Object.values(IndicatorType).map(t => (
                                    <MenuItem key={t} value={t}>{IndicatorTypeLabels[t] || t}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Signal</InputLabel>
                            <Select
                                value={signalFilter}
                                label="Signal"
                                onChange={(e) => { setSignalFilter(e.target.value); setPage(0); }}
                            >
                                <MenuItem value="">All Signals</MenuItem>
                                {Object.values(SignalType).map(sig => (
                                    <MenuItem key={sig} value={sig}>{SignalTypeLabels[sig] || sig}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Timeframe</InputLabel>
                            <Select
                                value={timeframeFilter}
                                label="Timeframe"
                                onChange={(e) => { setTimeframeFilter(e.target.value); setPage(0); }}
                            >
                                <MenuItem value="">All Timeframes</MenuItem>
                                {Object.values(TimeFrame).map(tf => (
                                    <MenuItem key={tf} value={tf}>{TimeFrameLabels[tf] || tf}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                                size="small"
                                onClick={handleClearFilters}
                                disabled={!hasActiveFilters}
                                sx={{ textTransform: 'none' }}
                            >
                                Clear
                            </Button>
                            <Tooltip title="Refresh"><IconButton onClick={fetchIndicators}><RefreshIcon /></IconButton></Tooltip>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* Error */}
            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Indicators Table */}
            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Symbol</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Timeframe</TableCell>
                                <TableCell>Parameters</TableCell>
                                <TableCell align="right">Latest Value</TableCell>
                                <TableCell>Signal</TableCell>
                                <TableCell>Last Calculated</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                                </TableRow>
                            ) : indicators.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <ShowChartIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                        <Typography color="text.secondary">No indicators configured</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                indicators.map((indicator) => (
                                    <TableRow key={indicator.id} hover>
                                        <TableCell>
                                            <Typography fontWeight="bold">{indicator.symbol || '-'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={IndicatorTypeLabels[indicator.indicator_type] || indicator.indicator_type}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={TimeFrameLabels[indicator.timeframe] || indicator.timeframe} size="small" />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {typeof indicator.parameters === 'object' ? JSON.stringify(indicator.parameters) : indicator.parameters || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography fontWeight="medium">
                                                {indicator.latest_value != null ? parseFloat(indicator.latest_value).toFixed(4) : '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {getSignalChip(indicator.latest_signal)}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {indicator.updated_at ? new Date(indicator.updated_at).toLocaleString() : 'Never'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                <Tooltip title="Calculate">
                                                    <IconButton size="small" onClick={() => handleCalculate(indicator)} disabled={calculating === indicator.id}>
                                                        {calculating === indicator.id ? <CircularProgress size={18} /> : <CalculateIcon />}
                                                    </IconButton>
                                                </Tooltip>
                                                {canDelete && (
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(indicator)}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                />
            </Paper>

            {/* Create Indicator Dialog */}
            <Dialog open={createDialogOpen} onClose={handleCreateClose} maxWidth="sm" fullWidth>
                <DialogTitle>Add Technical Indicator</DialogTitle>
                <DialogContent>
                    {formErrors.submit && <Alert severity="error" sx={{ mb: 2 }}>{formErrors.submit}</Alert>}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth error={!!formErrors.symbol_id} required>
                                <InputLabel>Symbol</InputLabel>
                                <Select value={formData.symbol_id} label="Symbol" onChange={handleFormChange('symbol_id')}>
                                    {symbols.map(sym => (
                                        <MenuItem key={sym.id} value={sym.id}>{sym.symbol} - {sym.company_name}</MenuItem>
                                    ))}
                                </Select>
                                {formErrors.symbol_id && <Typography variant="caption" color="error">{formErrors.symbol_id}</Typography>}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth error={!!formErrors.indicator_type} required>
                                <InputLabel>Indicator Type</InputLabel>
                                <Select value={formData.indicator_type} label="Indicator Type" onChange={handleFormChange('indicator_type')}>
                                    {Object.entries(IndicatorType).map(([key, value]) => (
                                        <MenuItem key={key} value={value}>{IndicatorTypeLabels[value]}</MenuItem>
                                    ))}
                                </Select>
                                {formErrors.indicator_type && <Typography variant="caption" color="error">{formErrors.indicator_type}</Typography>}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Timeframe</InputLabel>
                                <Select value={formData.timeframe} label="Timeframe" onChange={handleFormChange('timeframe')}>
                                    {Object.entries(TimeFrame).map(([key, value]) => (
                                        <MenuItem key={key} value={value}>{TimeFrameLabels[value]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Parameters (JSON)"
                                value={formData.parameters}
                                onChange={handleFormChange('parameters')}
                                multiline
                                rows={3}
                                error={!!formErrors.parameters}
                                helperText={formErrors.parameters || 'e.g. {"period": 14} for RSI'}
                                placeholder='{"period": 14}'
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCreateClose} disabled={submitting}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateSubmit} disabled={submitting}
                        sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
                        {submitting ? <CircularProgress size={24} /> : 'Create Indicator'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Indicator</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this <strong>{IndicatorTypeLabels[selectedIndicator?.indicator_type] || selectedIndicator?.indicator_type}</strong> indicator
                        for <strong>{selectedIndicator?.symbol}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default IndicatorsPage;
